/**
 * Device identity — generates and persists a UUID for anonymous user tracking.
 * Collects browser fingerprint signals for future multi-device sync.
 */

import type { FingerprintSignals } from '@skjv/common';
import { submitFingerprint } from '@api/conversations';

const DEVICE_ID_KEY = 'skjv-device-id';

/** Get or create a stable device ID (UUID) */
export function getDeviceId(): string {
  if (typeof localStorage === 'undefined') return '';
  let id = localStorage.getItem(DEVICE_ID_KEY);
  if (!id) {
    id = crypto.randomUUID();
    localStorage.setItem(DEVICE_ID_KEY, id);
  }
  return id;
}

/** Collect browser fingerprint signals */
export function getDeviceFingerprint(): FingerprintSignals {
  return {
    userAgent: navigator.userAgent,
    screenWidth: screen.width,
    screenHeight: screen.height,
    colorDepth: screen.colorDepth,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    language: navigator.language,
    platform: navigator.platform,
    hardwareConcurrency: navigator.hardwareConcurrency,
    deviceMemory: (navigator as { deviceMemory?: number }).deviceMemory,
    touchPoints: navigator.maxTouchPoints,
    pixelRatio: devicePixelRatio,
  };
}

const FINGERPRINT_SENT_KEY = 'skjv-fingerprint-sent';
const FINGERPRINT_INTERVAL_MS = 7 * 24 * 60 * 60 * 1000; // 1 week

/** Submit fingerprint to server if not sent recently. Fire-and-forget. */
export function submitFingerprintOnce(): void {
  if (typeof localStorage === 'undefined') return;
  const lastSent = localStorage.getItem(FINGERPRINT_SENT_KEY);
  if (lastSent && Date.now() - Number(lastSent) < FINGERPRINT_INTERVAL_MS) return;

  const deviceId = getDeviceId();
  if (!deviceId) return;

  submitFingerprint({ deviceId, signals: getDeviceFingerprint() })
    .then(() => localStorage.setItem(FINGERPRINT_SENT_KEY, String(Date.now())))
    .catch(() => { /* best-effort */ });
}
